import React, { useState, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight, Quote, Minus, SquareSplitVertical, ListOrdered, List, ChevronDown } from 'lucide-react';
import { trackManualFormatting } from '../../../utils/analytics';

interface EditorToolbarProps {
  editor: Editor;
  onImageUpload: () => void;
  onSave: () => void;
  onSaveAndContinue: () => void;
  onGoBack: () => void;
  onSplitAtCursor: () => void;
  isSaving: boolean;
  hasBookId: boolean;
  grammarErrorCount?: number;
  isCheckingGrammar?: boolean;
  onTriggerGrammarCheck?: () => void;
}

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return { open, setOpen, ref };
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editor, onImageUpload, onSave, onGoBack, onSplitAtCursor,
  onSaveAndContinue, isSaving, hasBookId,
  grammarErrorCount = 0, isCheckingGrammar = false, onTriggerGrammarCheck
}) => {
  const headingDropdown = useDropdown();
  const textStyleDropdown = useDropdown();
  const insertDropdown = useDropdown();
  const alignDropdown = useDropdown();

  // Derive active heading label
  const activeHeadingLabel = editor.isActive('heading', { level: 1 }) ? 'H1'
    : editor.isActive('heading', { level: 2 }) ? 'H2'
    : editor.isActive('heading', { level: 3 }) ? 'H3'
    : 'P';

  // Derive active text style label
  const activeTextStyleLabel = editor.isActive('bold') ? 'B'
    : editor.isActive('italic') ? 'I'
    : editor.isActive('underline') ? 'U'
    : editor.isActive('strike') ? 'S'
    : 'B';

  // Derive active alignment icon
  const activeAlignIcon = editor.isActive({ textAlign: 'center' }) ? <AlignCenter size={14} />
    : editor.isActive({ textAlign: 'right' }) ? <AlignRight size={14} />
    : <AlignLeft size={14} />;

  return (
    <div className="notion-toolbar">
      {/* Go Back + Undo/Redo — width aligned to sidebar */}
      <div className="toolbar-nav-group">
        <button className="toolbar-go-back-btn" onClick={onGoBack} title="Go Back">
          ← Go Back
        </button>
        <div className="toolbar-undo-redo">
          <button onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" className="toolbar-icon-btn">↩</button>
          <button onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" className="toolbar-icon-btn">↪</button>
        </div>
      </div>

      <div className="toolbar-divider toolbar-sidebar-divider" />

      {/* Heading dropdown: Aa: H1 ▾ */}
      <div className="toolbar-dropdown-wrapper" ref={headingDropdown.ref}>
        <button
          className={`toolbar-dropdown-trigger ${headingDropdown.open ? 'open' : ''}`}
          onClick={() => headingDropdown.setOpen(o => !o)}
          title="Heading style"
        >
          <span className="toolbar-dropdown-label-muted">Aa:</span> {activeHeadingLabel} <ChevronDown size={12} className="toolbar-chevron" />
        </button>
        {headingDropdown.open && (
          <div className="toolbar-dropdown-menu">
            <button className={`toolbar-dropdown-item ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleHeading({ level: 1 }).run(); trackManualFormatting('h1'); headingDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon" style={{ fontWeight: 700, fontSize: 15 }}>H1</span> Heading 1
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleHeading({ level: 2 }).run(); trackManualFormatting('h2'); headingDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon" style={{ fontWeight: 700, fontSize: 13 }}>H2</span> Heading 2
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleHeading({ level: 3 }).run(); trackManualFormatting('h3'); headingDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon" style={{ fontWeight: 700, fontSize: 11 }}>H3</span> Heading 3
            </button>
            <div className="toolbar-dropdown-divider" />
            <button className={`toolbar-dropdown-item ${!editor.isActive('heading') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().setParagraph().run(); headingDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon">¶</span> Paragraph
            </button>
          </div>
        )}
      </div>

      {/* Text style dropdown: Tt: B ▾ */}
      <div className="toolbar-dropdown-wrapper" ref={textStyleDropdown.ref}>
        <button
          className={`toolbar-dropdown-trigger ${textStyleDropdown.open ? 'open' : ''}`}
          onClick={() => textStyleDropdown.setOpen(o => !o)}
          title="Text style"
        >
          <span className="toolbar-dropdown-label-muted">Tt:</span> <strong>{activeTextStyleLabel}</strong> <ChevronDown size={12} className="toolbar-chevron" />
        </button>
        {textStyleDropdown.open && (
          <div className="toolbar-dropdown-menu">
            <button className={`toolbar-dropdown-item ${editor.isActive('bold') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleBold().run(); trackManualFormatting('bold'); textStyleDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><strong>B</strong></span> Bold
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('italic') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleItalic().run(); trackManualFormatting('italic'); textStyleDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><em>I</em></span> Italic
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('underline') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleUnderline().run(); trackManualFormatting('underline'); textStyleDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><u>U</u></span> Underline
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('strike') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleStrike().run(); trackManualFormatting('strikethrough'); textStyleDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><s>S</s></span> Strikethrough
            </button>
          </div>
        )}
      </div>

      {/* Insert dropdown */}
      <div className="toolbar-dropdown-wrapper" ref={insertDropdown.ref}>
        <button
          className={`toolbar-dropdown-trigger ${insertDropdown.open ? 'open' : ''}`}
          onClick={() => insertDropdown.setOpen(o => !o)}
          title="Insert block"
        >
          Insert <ChevronDown size={12} className="toolbar-chevron" />
        </button>
        {insertDropdown.open && (
          <div className="toolbar-dropdown-menu">
            <button className={`toolbar-dropdown-item ${editor.isActive('blockquote') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleBlockquote().run(); trackManualFormatting('blockquote'); insertDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><Quote size={14} /></span> Quote
            </button>
            <button className="toolbar-dropdown-item"
              onClick={() => { editor.chain().focus().setHorizontalRule().run(); trackManualFormatting('horizontal_rule'); insertDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><Minus size={14} /></span> Separator
            </button>
            <button className="toolbar-dropdown-item"
              onClick={() => { editor.chain().focus().setPageBreak().run(); trackManualFormatting('page_break'); insertDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><SquareSplitVertical size={14} /></span> Page Break
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('orderedList') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleOrderedList().run(); trackManualFormatting('ordered_list'); insertDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><ListOrdered size={14} /></span> List
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive('bulletList') ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().toggleBulletList().run(); trackManualFormatting('bullet_list'); insertDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><List size={14} /></span> Bullet Point
            </button>
          </div>
        )}
      </div>

      {/* Upload */}
      <div className="toolbar-group">
        <button onClick={onImageUpload} title="Upload Image" className="toolbar-upload-btn">
          + Upload
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Alignment dropdown */}
      <div className="toolbar-dropdown-wrapper" ref={alignDropdown.ref}>
        <button
          className={`toolbar-dropdown-trigger ${alignDropdown.open ? 'open' : ''}`}
          onClick={() => alignDropdown.setOpen(o => !o)}
          title="Text alignment"
        >
          {activeAlignIcon} <ChevronDown size={12} className="toolbar-chevron" />
        </button>
        {alignDropdown.open && (
          <div className="toolbar-dropdown-menu">
            <button className={`toolbar-dropdown-item ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().setTextAlign('left').run(); trackManualFormatting('align_left'); alignDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><AlignLeft size={14} /></span> Left
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().setTextAlign('center').run(); trackManualFormatting('align_center'); alignDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><AlignCenter size={14} /></span> Center
            </button>
            <button className={`toolbar-dropdown-item ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
              onClick={() => { editor.chain().focus().setTextAlign('right').run(); trackManualFormatting('align_right'); alignDropdown.setOpen(false); }}>
              <span className="toolbar-dropdown-item-icon"><AlignRight size={14} /></span> Right
            </button>
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Split Chapter */}
      <button className="toolbar-split-btn" onClick={() => { onSplitAtCursor(); trackManualFormatting('split_chapter'); }} title="Split into new chapter at cursor">
        <SquareSplitVertical size={13} strokeWidth={1.8} /> Split Chapter
      </button>

      <div className="toolbar-divider" />

      {/* Grammar */}
      <div className="toolbar-group">
        <button
          className={`grammar-check-btn ${grammarErrorCount > 0 ? 'has-errors' : ''}`}
          onClick={onTriggerGrammarCheck}
          disabled={isCheckingGrammar}
          title={isCheckingGrammar ? 'Checking grammar...' : `Grammar Check${grammarErrorCount > 0 ? ` (${grammarErrorCount} issues)` : ''}`}
        >
          {isCheckingGrammar ? 'Checking...' : 'Grammar'}
          {grammarErrorCount > 0 && !isCheckingGrammar && (
            <span className="grammar-error-badge">{grammarErrorCount}</span>
          )}
        </button>
      </div>

      <div className="toolbar-spacer" />

      {/* Save & Continue */}
      <button className="toolbar-save-btn" onClick={onSave} disabled={isSaving || !hasBookId} title={!hasBookId ? 'No book selected' : 'Save content'}>
        {isSaving ? 'Saving...' : 'Save'}
      </button>
      <button className="toolbar-continue-btn" onClick={onSaveAndContinue} disabled={isSaving || !hasBookId} title={!hasBookId ? 'No book selected' : 'Save and continue'}>
        {isSaving ? 'Saving...' : 'Continue'}
      </button>
    </div>
  );
};

export default EditorToolbar;
